import React, { useState, useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { getAvailableSlotsForReassign, reassignCandidateSlot } from "../../api/candidates.api";
import useApiToken from "../../hooks/useApiToken";

interface AvailableSlot {
  id: string;
  startTime: string;
  endTime: string;
  assessmentName: string;
  roleName: string;
  isActive: boolean;
}

interface ReassignSlotModalProps {
  isOpen: boolean;
  onClose: () => void;
  candidateId: string;
  candidateName: string;
  currentSlotId: string | null;
  roleStatusId: string;
  hasCurrentBooking: boolean;
  onReassignComplete: () => void;
}

const ReassignSlotModal: React.FC<ReassignSlotModalProps> = ({
  isOpen,
  onClose,
  candidateId,
  candidateName,
  currentSlotId,
  roleStatusId,
  hasCurrentBooking,
  onReassignComplete,
}) => {
  const token = useApiToken();
  const [availableSlots, setAvailableSlots] = useState<AvailableSlot[]>([]);
  const [selectedSlotId, setSelectedSlotId] = useState<string>("");
  const [cancelCurrentSlot, setCancelCurrentSlot] = useState<boolean>(true);
  const [bookNewSlot, setBookNewSlot] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && roleStatusId) {
      fetchAvailableSlots();
    }
  }, [isOpen, roleStatusId]);

  const fetchAvailableSlots = async () => {
    setLoading(true);
    setError(null);
    try {
      const slots = await getAvailableSlotsForReassign(roleStatusId, token!);
      // Filter out the current slot
      const filteredSlots = currentSlotId
        ? slots.filter((slot: AvailableSlot) => slot.id !== currentSlotId)
        : slots;
      setAvailableSlots(filteredSlots);
    } catch (err) {
      setError("Failed to fetch available slots");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!bookNewSlot && !cancelCurrentSlot) {
      setError("Please select at least one action");
      return;
    }

    if (bookNewSlot && !selectedSlotId) {
      setError("Please select a slot to book");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await reassignCandidateSlot(
        candidateId,
        cancelCurrentSlot ? currentSlotId : null,
        bookNewSlot ? selectedSlotId : null,
        cancelCurrentSlot && hasCurrentBooking,
        bookNewSlot,
        token!
      );
      onReassignComplete();
    } catch (err) {
      setError("Failed to reassign slot");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    const hasExplicitTz = /([zZ]|[+\-]\d{2}:?\d{2})$/.test(dateString);
    const utcString = hasExplicitTz ? dateString : `${dateString}Z`;
    const date = new Date(utcString);
    if (isNaN(date.getTime())) return "Invalid date";

    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white dark:bg-darkbglight rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
              onClick={onClose}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                Reassign Slot
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Reassign <span className="font-semibold">{candidateName}</span> to a different assessment slot
              </p>

              {error && (
                <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div className="mt-4 space-y-4">
                {/* Cancel current slot checkbox */}
                {hasCurrentBooking && (
                  <div className="flex items-center">
                    <input
                      id="cancelCurrentSlot"
                      type="checkbox"
                      checked={cancelCurrentSlot}
                      onChange={(e) => setCancelCurrentSlot(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <label
                      htmlFor="cancelCurrentSlot"
                      className="ml-2 text-sm text-gray-700 dark:text-gray-300"
                    >
                      Cancel current slot booking
                    </label>
                  </div>
                )}

                {/* Book new slot checkbox */}
                <div className="flex items-center">
                  <input
                    id="bookNewSlot"
                    type="checkbox"
                    checked={bookNewSlot}
                    onChange={(e) => setBookNewSlot(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <label
                    htmlFor="bookNewSlot"
                    className="ml-2 text-sm text-gray-700 dark:text-gray-300"
                  >
                    Book new slot
                  </label>
                </div>

                {/* Slot selection dropdown */}
                {bookNewSlot && (
                  <div className="mt-4">
                    <label
                      htmlFor="slotSelect"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Select New Slot
                    </label>
                    {loading ? (
                      <p className="mt-2 text-sm text-gray-500">Loading available slots...</p>
                    ) : availableSlots.length === 0 ? (
                      <p className="mt-2 text-sm text-gray-500">No available slots found</p>
                    ) : (
                      <select
                        id="slotSelect"
                        value={selectedSlotId}
                        onChange={(e) => setSelectedSlotId(e.target.value)}
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md dark:bg-darkbg dark:text-gray-300"
                      >
                        <option value="">Select a slot...</option>
                        {availableSlots.map((slot) => (
                          <option key={slot.id} value={slot.id}>
                            {formatDateTime(slot.startTime)} - {slot.assessmentName}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-darkbg px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting || (!cancelCurrentSlot && !bookNewSlot) || (bookNewSlot && !selectedSlotId)}
                  className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? "Processing..." : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReassignSlotModal;
